import React from 'react'
import { Avatar, Box, Typography, Chip } from '@mui/material'
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  HelpOutline as HelpOutlineIcon,
  Public as PublicIcon,
} from '@mui/icons-material'
import type { BookingInvitee, RoomBookingData } from '../../../../types/RoomBooking'
import ProfileAvatar from '../ProfileAvatar'
import { formatBookingTimeRange } from './bookingTimes'

export function formatDateHeader(dateStr: string): string {
  const d = new Date(dateStr)
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const target = new Date(d.getFullYear(), d.getMonth(), d.getDate())
  const diffDays = Math.round((target.getTime() - today.getTime()) / 86400000)
  if (diffDays === 0) return 'Today'
  if (diffDays === -1) return 'Yesterday'
  if (diffDays === 1) return 'Tomorrow'
  return d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' })
}

export const tzAbbr = new Intl.DateTimeFormat(undefined, { timeZoneName: 'short' })
  .formatToParts(new Date())
  .find((p) => p.type === 'timeZoneName')?.value || ''

export function formatTimeRange(startTime: string, endTime: string): string {
  return formatBookingTimeRange(startTime, endTime, tzAbbr)
}

export function getStatusColor(booking: RoomBookingData): string {
  if (booking.status === 'CANCELLED') return 'error.main'
  const now = new Date()
  const start = new Date(booking.startTime)
  const end = new Date(booking.endTime)
  if (now >= start && now < end) return 'success.main'
  if (now >= end) return 'text.disabled'
  return 'info.main'
}

export function getBookingStatusChip(booking: RoomBookingData) {
  if (booking.status === 'CANCELLED') return <Chip label="Cancelled" size="small" color="error" variant="outlined" />
  const now = new Date()
  const start = new Date(booking.startTime)
  const end = new Date(booking.endTime)
  if (now >= start && now < end) return <Chip label="Active" size="small" color="success" variant="outlined" />
  if (now >= end) return <Chip label="Completed" size="small" variant="outlined" />
  return <Chip label="Upcoming" size="small" color="info" variant="outlined" />
}

export const statusIcon: Record<string, React.ReactNode> = {
  accepted: <CheckCircleIcon sx={{ fontSize: 14, color: 'success.main' }} />,
  declined: <CancelIcon sx={{ fontSize: 14, color: 'error.main' }} />,
  maybe: <HelpOutlineIcon sx={{ fontSize: 14, color: 'warning.main' }} />,
  pending: <Chip label="pending" size="small" variant="outlined" sx={{ height: 18, fontSize: 11 }} />,
}

export function InviteeList({ invitees, currentUserId }: { invitees: RoomBookingData['invitees']; currentUserId: string }) {
  return (
    <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      {invitees.map((inv, i) => (
        <Box key={inv.userId || i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {statusIcon[inv.status] || statusIcon.pending}
          <Typography variant="caption">
            {inv.name || inv.email || 'Unknown'}
            {inv.userId === currentUserId && ' (you)'}
          </Typography>
        </Box>
      ))}
    </Box>
  )
}

type BookingInviteeAvatarProps = { invitee: BookingInvitee; size?: number } & Omit<
  React.HTMLAttributes<HTMLElement>,
  'children'
>

/**
 * A member's photo or pattern. An external guest has no profile, so they get a globe (as in the
 * booking form) instead of a pattern that would pass them off as a member.
 */
export const BookingInviteeAvatar = React.forwardRef<HTMLElement, BookingInviteeAvatarProps>(
  function BookingInviteeAvatar({ invitee, size = 24, ...rest }, ref) {
    const name = invitee.name || invitee.email || 'Unknown'
    if (invitee.type === 'external') {
      return (
        <Avatar
          {...rest}
          ref={ref as React.Ref<HTMLDivElement>}
          role="img"
          aria-label={`${name} (external guest)`}
          data-testid="external-guest-avatar"
          sx={{ width: size, height: size, bgcolor: 'var(--sevv-primary, #f97415)' }}
        >
          <PublicIcon sx={{ fontSize: Math.round(size * 0.65) }} />
        </Avatar>
      )
    }
    return (
      <ProfileAvatar
        {...rest}
        ref={ref}
        userId={invitee.userId}
        name={name}
        photoUrl={invitee.customAvatarUrl}
        patternStyle={invitee.avatarVariant}
        size={size}
      />
    )
  }
)
